import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  MapPin, Calendar, Users, DollarSign, ArrowRight, 
  SlidersHorizontal, Star, Hotel, Plane, Car, Award, Filter, TrendingUp 
} from "lucide-react";
import { useTourismPackagesReal } from "@/hooks/useTourismPackagesReal";
import { usePackageFilters } from "@/hooks/usePackageFilters";
import { PackageFiltersAdvanced } from "@/components/tourism-packages/PackageFiltersAdvanced";
import { getStatusColor } from "@/components/tourism-packages/utils";
import { PageWrapper } from "@/components/layout/PageWrapper";

const PackageResults = () => {
  const [searchParams] = useSearchParams();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
  const [minRating, setMinRating] = useState<number>(0);

  const { packages, isLoading } = useTourismPackagesReal();
  const { filters, setFilters, filteredPackages: baseFiltered, resetFilters, totalResults } = usePackageFilters(packages || []);

  // Get search parameters
  const searchCountryId = searchParams.get('countryId');
  const searchDestination = searchParams.get('destination');
  const checkIn = searchParams.get('checkin');
  const checkOut = searchParams.get('checkout');
  const adults = searchParams.get('adults') || '1';
  const children = searchParams.get('children') || '0';
  const rooms = searchParams.get('rooms') || '1';

  // Apply search filters on mount
  useEffect(() => {
    if (searchCountryId) {
      setFilters(prev => ({ ...prev, countryId: searchCountryId }));
    }
    if (searchDestination) {
      setFilters(prev => ({ ...prev, search: searchDestination }));
    }
  }, [searchCountryId, searchDestination]);

  // Apply additional filters (price, rating)
  const filteredPackages = baseFiltered.filter(pkg => {
    if (pkg.price < priceRange[0] || pkg.price > priceRange[1]) return false;
    if (minRating > 0 && (pkg.rating || 0) < minRating) return false;
    return true;
  });

  // Sort packages
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    switch (sortBy) {
      case 'price_low':
        return a.price - b.price;
      case 'price_high':
        return b.price - a.price;
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      default:
        return 0;
    }
  });

  const searchSummary = `${adults} بالغ${children !== '0' ? ` • ${children} طفل` : ''} • ${rooms} ${rooms === '1' ? 'غرفة' : 'غرف'}`;

  if (isLoading) {
    return (
      <PageWrapper title="نتائج البحث" subtitle="جاري البحث عن الباقات المناسبة...">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">جاري تحميل الباقات...</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      title="نتائج البحث"
      subtitle={`عثرنا على ${sortedPackages.length} باقة سياحية`}
      maxWidth="2xl"
    >
      {/* Search Summary */}
      <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-2">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="w-4 h-4" />
                {checkIn && checkOut ? (
                  <span>
                    {new Date(checkIn).toLocaleDateString('ar-SA')} - {new Date(checkOut).toLocaleDateString('ar-SA')}
                  </span>
                ) : (
                  <span>لم يتم تحديد التواريخ</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{searchSummary}</span>
              </div>
              {searchDestination && (
                <div className="flex items-center gap-2 text-sm font-medium">
                  <MapPin className="w-4 h-4" />
                  <span>{searchDestination}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Link to="/package-search">
                <Button variant="outline" size="sm">
                  <ArrowRight className="w-4 h-4 ml-2" />
                  تعديل البحث
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <SlidersHorizontal className="w-4 h-4 ml-2" />
                {showAdvancedFilters ? 'إخفاء الفلاتر' : 'فلاتر متقدمة'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <PackageFiltersAdvanced
          filters={filters}
          onFiltersChange={setFilters}
          onReset={resetFilters}
          totalResults={totalResults}
        />
      )}

      {/* Sort & Filters Row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SlidersHorizontal className="h-5 w-5" />
                فلترة النتائج
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sort By */}
              <div>
                <label className="text-sm font-medium mb-2 block">ترتيب حسب</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">الأكثر صلة</SelectItem>
                    <SelectItem value="price_low">الأقل سعراً</SelectItem>
                    <SelectItem value="price_high">الأعلى سعراً</SelectItem>
                    <SelectItem value="rating">الأعلى تقييماً</SelectItem>
                    <SelectItem value="newest">الأحدث</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  نطاق السعر: {priceRange[0]} - {priceRange[1]} ريال
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={(value) => setPriceRange(value as [number, number])}
                  min={0}
                  max={50000}
                  step={500}
                  className="mt-2"
                />
              </div>

              {/* Minimum Rating */}
              <div>
                <label className="text-sm font-medium mb-2 block">التقييم الأدنى</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setMinRating(rating === minRating ? 0 : rating)}
                      className={`p-2 rounded ${
                        rating <= minRating
                          ? 'bg-yellow-400 text-white'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Star className="h-4 w-4" fill="currentColor" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Reset Filters */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setPriceRange([0, 50000]);
                  setMinRating(0);
                  setSortBy("relevance");
                }}
              >
                إعادة تعيين الفلاتر
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Results Grid */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              عرض {sortedPackages.length} نتيجة
            </p>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">الأكثر ملاءمة</SelectItem>
                <SelectItem value="price_low">الأقل سعراً</SelectItem>
                <SelectItem value="price_high">الأعلى سعراً</SelectItem>
                <SelectItem value="rating">الأعلى تقييماً</SelectItem>
                <SelectItem value="newest">الأحدث</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {sortedPackages.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <Filter className="w-16 h-16 text-muted-foreground mx-auto" />
                <h3 className="text-xl font-semibold">لم نعثر على نتائج</h3>
                <p className="text-muted-foreground">
                  جرب تعديل معايير البحث أو الفلاتر
                </p>
                <Link to="/package-search">
                  <Button>بحث جديد</Button>
                </Link>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sortedPackages.map((pkg) => {
                const details = pkg.package_details as any;
                return (
                  <Link key={pkg.id} to={`/package-details/${pkg.id}`}>
                    <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden rounded-t-lg">
                        {pkg.images && pkg.images.length > 0 ? (
                          <img src={pkg.images[0]} alt={pkg.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                            <MapPin className="w-16 h-16 text-muted-foreground" />
                          </div>
                        )}
                        <Badge className={`absolute top-2 right-2 ${getStatusColor(pkg.status)}`}>
                          {pkg.status === 'active' ? 'نشط' : 'منتهي'}
                        </Badge>
                      </div>

                      <CardHeader>
                        <CardTitle className="text-lg line-clamp-2">{pkg.title}</CardTitle>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          {pkg.destination}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {pkg.duration} ليالي
                        </div>
                      </CardHeader>

                      <CardContent>
                        {/* Features */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {details?.includesHotels && <Badge variant="outline"><Hotel className="w-3 h-3 ml-1" />فنادق</Badge>}
                          {details?.includesInternationalFlights && <Badge variant="outline"><Plane className="w-3 h-3 ml-1" />طيران</Badge>}
                          {details?.includesTransport && <Badge variant="outline"><Car className="w-3 h-3 ml-1" />مواصلات</Badge>}
                        </div>

                        {/* Price */}
                        <div className="flex items-center justify-between pt-4 border-t">
                          <div>
                            <p className="text-xs text-muted-foreground">السعر يبدأ من</p>
                            <p className="text-2xl font-bold text-primary">
                              {pkg.price.toLocaleString()} <span className="text-sm">ريال</span>
                            </p>
                          </div>
                          <Button size="sm">عرض التفاصيل</Button>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default PackageResults;